import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DataTable, DataTableColumn } from "@/components/ui/DataTable";
import { 
  AlertTriangle, 
  Calendar, 
  RefreshCw
} from "lucide-react";
import { useHolidayOverrides } from "@/hooks/useDutyRosterManagement";

interface HolidayOverrideModalProps {
  groupId: string;
  monthYear: string;
  isOpen: boolean;
  onClose: () => void;
}

export function HolidayOverrideModal({ 
  groupId, 
  monthYear, 
  isOpen, 
  onClose 
}: HolidayOverrideModalProps) {
  const {
    overrides,
    overridesLoading,
    detectCollisions,
    isDetecting: isDetectingCollisions
  } = useHolidayOverrides(groupId, monthYear);

  const handleDetectCollisions = async () => {
    try {
      await detectCollisions();
    } catch (error) {
      console.error('Error detecting collisions:', error);
      // Error handling is done in the hook
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Flatten data for better sorting
  const flattenedOverrides = overrides.map(override => ({
    ...override,
    group_name: override.emp_groups?.group_name || 'Unknown Group',
    holiday_name: override.holidays?.name || 'Unknown Holiday'
  }));

  // Define columns for the DataTable
  const columns: DataTableColumn<any>[] = [
    {
      key: 'group_name',
      header: 'Group',
      sortable: true,
      searchable: true,
      render: (value) => <span className="font-medium">{value as string}</span>
    },
    {
      key: 'org_off_day',
      header: 'Original Off Day',
      sortable: true,
      render: (value) => formatDate(value as string)
    },
    {
      key: 'holiday_name',
      header: 'Holiday',
      sortable: true,
      searchable: true,
      render: (value) => value as string
    },
    {
      key: 'off_day_shifted_to',
      header: 'Shifted To',
      sortable: true,
      render: (value) => formatDate(value as string)
    },
    {
      key: 'month_year',
      header: 'Month',
      sortable: true,
      searchable: true
    }
  ];


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[70vh] overflow-hidden">
        <DialogHeader className="pb-4">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Active Holiday Collisions {new Date().getFullYear()} ({overrides.length})
            </DialogTitle>
            <Button
              onClick={handleDetectCollisions}
              disabled={isDetectingCollisions}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 bg-white hover:bg-gray-50 border-gray-200"
            >
              <RefreshCw className={`h-4 w-4 ${isDetectingCollisions ? 'animate-spin' : ''}`} />
              Detect Collisions
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {overrides.length === 0 && !overridesLoading ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <Calendar className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Holiday Collisions Found</h3>
              <p className="text-sm text-gray-500">All holidays are properly aligned with working days</p>
            </div>
          ) : (
            <div className="rounded-lg border bg-card">
              <DataTable
                data={flattenedOverrides}
                columns={columns}
                isLoading={overridesLoading}
                config={{
                  searchPlaceholder: "Search by group, holiday, or month...",
                  defaultSortField: 'org_off_day',
                  defaultSortDirection: 'asc',
                  rowsPerPage: 8,
                  showSearch: true,
                  showColumnVisibility: false,
                  showExport: false,
                  showPagination: true,
                  showRefresh: false,
                  emptyStateMessage: "No holiday collisions found",
                  noSearchResultsMessage: "No collisions match your search criteria"
                }}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}